import type { PermissionKey } from '@didto/domain';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSession } from './session';
import { EmptyState, Loading as LoadingLine } from '@didto/ui';

/**
 * Route guards. They gate navigation only; the server re-checks every permission.
 *  - RequireHuman: an owner/manager browser login.
 *  - RequireDeviceSession: an enrolled device with an open staff PIN session (POS/KDS surfaces).
 *  - RequirePermission: any principal whose effective permissions include `permission`.
 */
export function RequireHuman({ children }: { children: ReactNode }) {
  const s = useSession();
  const loc = useLocation();
  if (!s.ready) return <Loading />;
  if (!s.authed) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  if (s.me?.principal === 'device')
    return <Navigate to={s.me.terminal?.landing ?? '/lock'} replace />;
  if (s.me?.principal !== 'human')
    return (
      <NoAccess reason="This login is not linked to an employee. Ask the owner to link it under Employees." />
    );
  if (s.me.mfa_enrolled && s.me.aal !== 'aal2') return <Navigate to="/mfa" replace />;
  return <>{children}</>;
}

export function RequireDeviceSession({
  permission,
  children,
}: {
  permission: PermissionKey;
  children: ReactNode;
}) {
  const s = useSession();
  if (!s.ready) return <Loading />;
  if (!s.authed) return <Navigate to="/enroll" replace />;
  if (s.me?.principal === 'human') {
    // Owners may preview operational surfaces from a browser if they hold the permission.
    return s.can(permission) ? (
      <>{children}</>
    ) : (
      <NoAccess reason="Your role does not include this surface." />
    );
  }
  if (s.me?.principal !== 'device') return <Navigate to="/enroll" replace />;
  if (!s.me.terminal_session) return <Navigate to="/lock" replace />;
  if (!s.can(permission)) return <NoAccess reason="Your role does not include this surface." />;
  return <>{children}</>;
}

export function RequirePermission({
  permission,
  children,
}: {
  permission: PermissionKey;
  children: ReactNode;
}) {
  const s = useSession();
  const loc = useLocation();
  if (!s.ready) return <Loading />;
  if (!s.authed) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  if (s.me?.principal === 'device' && !s.me.terminal_session)
    return <Navigate to="/lock" replace />;
  if (s.me?.principal === 'human' && s.me.mfa_enrolled && s.me.aal !== 'aal2')
    return <Navigate to="/mfa" replace />;
  if (!s.can(permission)) return <NoAccess reason="Your role does not include this area." />;
  return <>{children}</>;
}

export function Loading() {
  return (
    <div className="d-page" aria-live="polite">
      <LoadingLine />
    </div>
  );
}

/** A screen this login or role may not open: said plainly, with who can change it. */
export function NoAccess({ reason }: { reason: string }) {
  return (
    <div className="d-page">
      <EmptyState title="No access" testId="no-access">
        {reason}
      </EmptyState>
    </div>
  );
}
